import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PublicNavbar } from "@/components/layout/PublicNav";
import { AppFooter } from "@/components/layout/AppFooter";
import { GlowField } from "@/components/ui/GlowField";
import { CtaButton } from "@/components/ui/CtaButton";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    if (!token) return toast.error("Reset link is invalid");
    setLoading(true);
    try {
      await api.post("/auth/reset", { token, password });
      toast.success("Password updated — please sign in");
      nav("/login");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFAF7] text-[#0A0C10]">
      <PublicNavbar />
      <div className="relative flex-1 flex items-center overflow-hidden">
        <div aria-hidden className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -left-16 w-[420px] h-[420px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(0,47,167,0.20), transparent 60%)" }} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
          className="relative w-full max-w-md mx-auto px-6 py-12"
        >
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#FFB400] inline-flex items-center gap-2 mb-3">
            <ShieldCheck size={12} /> Reset password
          </div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Choose a new password.</h1>
          <p className="mt-2 text-neutral-600 text-[15px]">Make it at least 6 characters. Use something unique.</p>

          <form onSubmit={submit} className="bg-white border border-black/8 rounded-2xl mt-8 p-6 sm:p-8 space-y-4 shadow-[0_30px_80px_-20px_rgba(10,12,16,0.12)]">
            <GlowField label="New password" testid="reset-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} />
            <GlowField label="Confirm password" testid="reset-confirm" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            <CtaButton type="submit" variant="primary" size="lg" disabled={loading} className="w-full rounded-full" data-testid="reset-submit">
              <KeyRound size={14} /> {loading ? "Updating…" : "Update password"}
            </CtaButton>
            <div className="text-center text-sm text-neutral-600">
              <Link to="/login" className="font-semibold text-[#0A0C10] underline underline-offset-4 decoration-[#FFB400]/50 hover:text-[#FFB400] transition-colors">Back to sign in</Link>
            </div>
          </form>
        </motion.div>
      </div>
      <AppFooter variant="light" />
    </div>
  );
}
